import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";
import ThemePicker from "@/components/dashboard/ThemePicker";
import { Users, Crown, ChartLineUp, Hourglass, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";

const CUR = { usd: "$", sek: " kr", eur: "€" };
const fmtMoney = (amount, cur) =>
  cur === "usd" || cur === "eur" ? `${CUR[cur]}${amount.toLocaleString()}` : `${amount.toLocaleString()}${CUR[cur] || " " + cur.toUpperCase()}`;

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.get("/admin/revenue")
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.status === 403 ? "forbidden" : "failed"));
  };
  useEffect(load, []);

  return (
    <div className="min-h-screen bg-bg text-ink" data-testid="admin-revenue-page">
      <header className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-2">
          <BrandMark size={32} />
          <span className="font-display text-xl font-semibold tracking-tight">Ikavio</span>
        </Link>
        <ThemePicker />
      </header>
      <main className="max-w-4xl mx-auto px-6 pb-24">
        <span className="label-eyebrow">Founder only</span>
        <h1 className="font-display font-light tracking-tighter text-4xl sm:text-5xl mt-2">Revenue snapshot</h1>

        {error === "forbidden" && (
          <div className="clay p-10 mt-8 text-center" data-testid="admin-forbidden">
            <p className="font-display text-xl font-light">This page is private</p>
            <p className="text-sm text-muted2 mt-2">Only founder accounts can view revenue. <Link to="/app" className="text-brand hover:underline">Back to the app</Link></p>
          </div>
        )}
        {error === "failed" && (
          <div className="clay p-8 mt-8 text-center text-sm text-muted2" data-testid="admin-error">
            Couldn't load revenue data. <button onClick={load} className="text-brand hover:underline font-medium">Retry</button>
          </div>
        )}
        {!data && !error && (
          <div className="clay p-8 mt-8 flex items-center gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-brand border-t-transparent animate-spin" />
            <span className="text-sm text-muted2">Fetching live numbers from Stripe…</span>
          </div>
        )}

        {data && (
          <>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs font-medium bg-secondary rounded-full px-3 py-1 uppercase tracking-wide" data-testid="admin-mode">{data.mode} mode</span>
              <button onClick={load} data-testid="admin-refresh-btn" className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline font-medium">
                <ArrowClockwise size={13} weight="bold" /> Refresh
              </button>
            </div>

            <div className="clay p-8 mt-6" data-testid="admin-mrr-card">
              <span className="label-eyebrow">Monthly recurring revenue</span>
              <div className="flex items-end gap-6 flex-wrap mt-3">
                {Object.keys(data.mrr).length === 0 ? (
                  <span className="font-display text-4xl font-light text-muted2">0</span>
                ) : (
                  Object.entries(data.mrr).map(([cur, amount]) => (
                    <div key={cur} data-testid={`admin-mrr-${cur}`}>
                      <span className="font-display text-4xl sm:text-5xl font-light text-brand">{fmtMoney(amount, cur)}</span>
                      <span className="text-sm text-muted2 ml-2">/mo · {cur.toUpperCase()}</span>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-muted2 mt-3">Annual plans counted at 1/12 per month. Intro first-month discounts don't reduce MRR.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <Tile icon={Crown} color="var(--brand)" label="Pro subscribers" value={data.pro_subscribers} sub={`${data.active_stripe_subscriptions} active in Stripe`} testId="admin-subscribers" />
              <Tile icon={Users} color="var(--sage)" label="Total members" value={data.total_members} sub="all accounts" testId="admin-members" />
              <Tile icon={Hourglass} color="#E8A54B" label="Active trials" value={data.active_trials} sub="in their first 7 days" testId="admin-trials" />
              <Tile icon={ChartLineUp} color="#8B7CF6" label="Paid checkouts" value={data.paid_checkouts_total} sub={`${data.plan_split.monthly} monthly · ${data.plan_split.annual} annual`} testId="admin-checkouts" />
            </div>

            <p className="text-[11px] text-muted2 mt-6">Snapshot generated {new Date(data.generated_at).toLocaleString()}. Live from Stripe + your member database.</p>
          </>
        )}

        {error !== "forbidden" && <AnalyticsPanel />}
      </main>
    </div>
  );
}

function Tile({ icon: Icon, color, label, value, sub, testId }) {
  return (
    <div className="clay p-5" data-testid={testId}>
      <Icon size={18} weight="fill" color={color} />
      <div className="font-display text-3xl font-light tracking-tight mt-2">{value}</div>
      <div className="text-xs text-muted2 mt-0.5">{label}</div>
      <div className="text-[11px] text-muted2/80 mt-0.5">{sub}</div>
    </div>
  );
}
